import { PaperData } from '../types/paper';

export const seedAttentionIsAllYouNeed: PaperData = {
  id: 'arxiv-1706-03762',
  title: 'Attention Is All You Need',
  authors: [
    'Ashish Vaswani',
    'Noam Shazeer',
    'Niki Parmar',
    'Jakob Uszkoreit',
    'Llion Jones',
    'Aidan N. Gomez',
    'Łukasz Kaiser',
    'Illia Polosukhin'
  ],
  year: '2017',
  arxivId: '1706.03762',
  pdfUrl: 'https://arxiv.org/pdf/1706.03762.pdf',
  abstract: 'The dominant sequence transduction models are based on complex recurrent or convolutional neural networks in an encoder-decoder configuration. The best performing models also connect the encoder and decoder through an attention mechanism. We propose a new simple network architecture, the Transformer, based solely on attention mechanisms, dispensing with recurrence and convolutions entirely. Experiments on two machine translation tasks show these models to be superior in quality while being more parallelizable and requiring significantly less time to train.',
  
  lineageGraph: {
    nodes: [
      // Ancestors
      {
        id: 'rnn-lstm-1997',
        label: 'LSTM / Recurrent Neural Networks',
        type: 'ancestor',
        year: '1997',
        authors: 'Hochreiter & Schmidhuber',
        description: 'Sequential processing $O(N)$ time steps, causing gradient vanishing and memory bottlenecks across long token distances.',
        keyInnovation: 'Gated memory cells for sequential state persistence.'
      },
      {
        id: 'bahdanau-attention-2014',
        label: 'Additive Attention (Bahdanau)',
        type: 'ancestor',
        year: '2014',
        authors: 'Bahdanau et al.',
        description: 'First dynamic alignment mechanism for Seq2Seq, replacing fixed vector bottleneck with additive scoring function $\\text{score}(s_t, h_i) = v_a^T \\tanh(W_a s_t + U_a h_i)$.',
        keyInnovation: 'Dynamic context vectors via learned query-key alignments.'
      },
      {
        id: 'luong-attention-2015',
        label: 'Multiplicative Attention (Luong)',
        type: 'ancestor',
        year: '2015',
        authors: 'Luong et al.',
        description: 'Simplified dot-product attention $\\text{score}(s_t, h_i) = s_t^T W_a h_i$, faster than additive attention but prone to variance exploding for high dimensions.',
        keyInnovation: 'Dot-product scoring for matrix-multiplication hardware efficiency.'
      },
      {
        id: 'convs2s-2017',
        label: 'ConvS2S & ByteNet',
        type: 'ancestor',
        year: '2017',
        authors: 'Gehring et al. / Kalchbrenner et al.',
        description: 'Replaced RNNs with 1D convolutions to parallelize training, but maximum path length grew logarithmically or linearly with filter size.',
        keyInnovation: 'Parallel feed-forward sequence modeling using causal dilated convolutions.'
      },
      {
        id: 'layer-norm-2016',
        label: 'Layer Normalization',
        type: 'ancestor',
        year: '2016',
        authors: 'Ba et al.',
        description: 'Normalizes activations across features for each individual training sample, crucial for stabilizing deep Transformer residual stacks.',
        keyInnovation: 'Batch-independent activation variance stabilization.'
      },

      // Target Paper
      {
        id: 'transformer-2017',
        label: 'The Transformer Architecture',
        type: 'target_paper',
        year: '2017',
        authors: 'Vaswani et al. (Google Brain / Research)',
        description: 'Dispensed with recurrence and convolution entirely. Introduced Scaled Dot-Product Attention, Multi-Head Attention, and Sinusoidal Positional Encodings to achieve $O(1)$ sequential operations per layer.',
        keyInnovation: 'Pure Self-Attention architecture enabling complete parallel training.'
      },

      // Descendants
      {
        id: 'bert-2018',
        label: 'BERT (Bidirectional Encoder)',
        type: 'descendant',
        year: '2018',
        authors: 'Devlin et al. (Google)',
        description: 'Uses Transformer Encoder stack with Masked Language Modeling (MLM) for deep bidirectional representation learning.',
        keyInnovation: 'Bidirectional contextual pre-training for NLU downstream fine-tuning.'
      },
      {
        id: 'gpt-2018',
        label: 'GPT Series (Autoregressive Decoder)',
        type: 'descendant',
        year: '2018',
        authors: 'Radford et al. (OpenAI)',
        description: 'Uses masked Transformer Decoder stack for generative autoregressive language modeling scaled from GPT-1 to GPT-4.',
        keyInnovation: 'Zero-shot and few-shot task generation via massive autoregressive pre-training.'
      },
      {
        id: 'transformer-xl-2019',
        label: 'Transformer-XL & Recurrence',
        type: 'descendant',
        year: '2019',
        authors: 'Dai et al.',
        description: 'Introduced segment-level recurrence and relative positional encodings to process arbitrarily long context windows.',
        keyInnovation: 'KV state caching across context chunks.'
      },
      {
        id: 'rope-2021',
        label: 'RoPE (Rotary Position Embedding)',
        type: 'descendant',
        year: '2021',
        authors: 'Su et al.',
        description: 'Encodes relative position by rotating Query and Key vectors in complex space: $R_{\\Theta, m}^d Q$, now used in LLaMA and Qwen.',
        keyInnovation: 'Complex rotation-based relative position encoding.'
      },
      {
        id: 'flash-attention-2022',
        label: 'FlashAttention (IO-Aware Self-Attention)',
        type: 'descendant',
        year: '2022',
        authors: 'Dao et al. (Stanford)',
        description: 'Reorganized self-attention computation using SRAM tiling and online softmax to avoid $O(N^2)$ memory reads/writes to HBM.',
        keyInnovation: 'Exact attention speedup (2-4x) with linear memory overhead $O(N)$.'
      },
      {
        id: 'gqa-2023',
        label: 'Grouped-Query Attention (GQA)',
        type: 'descendant',
        year: '2023',
        authors: 'Ainslie et al.',
        description: 'Partitioned $H$ query heads into $G$ groups sharing $1$ Key/Value head, drastically reducing KV cache size during LLM generation.',
        keyInnovation: 'Memory-efficient inference balancing MHA accuracy and MQA speed.'
      }
    ],
    links: [
      { source: 'rnn-lstm-1997', target: 'transformer-2017', label: 'Replaced sequential bottleneck' },
      { source: 'bahdanau-attention-2014', target: 'transformer-2017', label: 'Evolved additive to scaled dot-product' },
      { source: 'luong-attention-2015', target: 'transformer-2017', label: 'Scaled dot-product normalization' },
      { source: 'convs2s-2017', target: 'transformer-2017', label: 'Achieved O(1) path length' },
      { source: 'layer-norm-2016', target: 'transformer-2017', label: 'Stabilized residual blocks' },
      { source: 'transformer-2017', target: 'bert-2018', label: 'Encoder-only stack' },
      { source: 'transformer-2017', target: 'gpt-2018', label: 'Causal decoder stack' },
      { source: 'transformer-2017', target: 'transformer-xl-2019', label: 'Context window extension' },
      { source: 'transformer-2017', target: 'rope-2021', label: 'Positional encoding evolution' },
      { source: 'transformer-2017', target: 'flash-attention-2022', label: 'Hardware-level SRAM optimization' },
      { source: 'transformer-2017', target: 'gqa-2023', label: 'Inference KV cache reduction' }
    ]
  },

  concepts: [
    {
      id: 'concept-scaled-dot-product',
      title: 'Scaled Dot-Product Attention',
      category: 'core_innovation',
      definition: 'An attention mechanism that computes alignment scores between queries and keys using inner products, scaled down by $\\sqrt{d_k}$ to prevent gradient vanishing in the softmax function.',
      mathFormulas: [
        {
          label: 'Attention Matrix Formula',
          latex: '\\text{Attention}(Q, K, V) = \\text{softmax}\\left(\\frac{Q K^T}{\\sqrt{d_k}}\\right) V',
          explanation: 'Where $Q \\in \\mathbb{R}^{N \\times d_k}$, $K \\in \\mathbb{R}^{M \\times d_k}$, and $V \\in \\mathbb{R}^{M \\times d_v}$. $N$ is query length, $M$ is key/value sequence length.'
        },
        {
          label: 'Scaling Factor Justification',
          latex: '\\text{Var}(q \\cdot k) = d_k \\quad \\implies \\quad \\text{Var}\\left(\\frac{q \\cdot k}{\\sqrt{d_k}}\\right) = 1',
          explanation: 'Assuming $q_i, k_i$ are independent random variables with mean 0 and variance 1, their dot product has variance $d_k$. For large $d_k$, scores explode, pushing softmax into regions with extremely small gradients.'
        }
      ],
      intuition: 'Think of queries as search terms, keys as library catalog tags, and values as the book contents. Dot product measures term-tag similarity. Scaling ensures the similarity scores stay within a stable dynamic range so softmax produces healthy backpropagation gradients.',
      keyNuances: [
        'Without scaling ($\\sqrt{d_k}=1$), for large dimensions like $d_k=64$ or $128$, softmax outputs turn into one-hot vectors, collapsing gradients.',
        'Additive attention performs better for very large $d_k$ without scaling, but Scaled Dot-Product is significantly faster due to highly optimized BLAS matrix multiplication.'
      ],
      codeSnippet: {
        language: 'python',
        code: `import torch
import torch.nn.functional as F

def scaled_dot_product_attention(Q, K, V, mask=None):
    # Q, K, V dimensions: [batch_size, num_heads, seq_len, d_k]
    d_k = Q.size(-1)
    scores = torch.matmul(Q, K.transpose(-2, -1)) / (d_k ** 0.5)
    
    if mask is not None:
        scores = scores.masked_fill(mask == 0, -1e9)
        
    attn_weights = F.softmax(scores, dim=-1)
    output = torch.matmul(attn_weights, V)
    return output, attn_weights`,
        explanation: 'PyTorch implementation showing matrix multiplication, scaling, causal masking, softmax, and value aggregation.'
      },
      ancestors: [
        {
          paperTitle: 'Neural Machine Translation by Jointly Learning to Align and Translate',
          authorsYear: 'Bahdanau et al., 2014',
          concept: 'Additive Attention',
          relationship: 'Replaced feed-forward network alignment score with dot product for $O(N)$ speed improvement.'
        },
        {
          paperTitle: 'Effective Approaches to Attention-based Neural Machine Translation',
          authorsYear: 'Luong et al., 2015',
          concept: 'Multiplicative Attention',
          relationship: 'Added the critical $1/\\sqrt{d_k}$ variance scaling factor.'
        }
      ],
      descendants: [
        {
          concept: 'FlashAttention',
          paperOrField: 'Dao et al., 2022',
          impact: 'Fuses scaling, masking, and softmax into a single CUDA kernel using SRAM tiling.'
        }
      ],
      citations: ['Vaswani et al., 2017, Section 3.2.1']
    },

    {
      id: 'concept-multi-head-attention',
      title: 'Multi-Head Attention (MHA)',
      category: 'architectural_component',
      definition: 'An extension of self-attention that projects Queries, Keys, and Values into $h$ lower-dimensional subspaces in parallel, allowing the model to jointly attend to information from different representation subspaces at different positions.',
      mathFormulas: [
        {
          label: 'Multi-Head Attention Equation',
          latex: '\\text{MultiHead}(Q, K, V) = \\text{Concat}(\\text{head}_1, \\dots, \\text{head}_h) W^O',
          explanation: 'Where $W^O \\in \\mathbb{R}^{h d_v \\times d_{\\text{model}}}$ projects concatenated head outputs back to model dimension.'
        },
        {
          label: 'Individual Head Equation',
          latex: '\\text{head}_i = \\text{Attention}\\left(Q W_i^Q, K W_i^K, V W_i^V\\right)',
          explanation: 'Projections: $W_i^Q \\in \\mathbb{R}^{d_{\\text{model}} \\times d_k}$, $W_i^K \\in \\mathbb{R}^{d_{\\text{model}} \\times d_k}$, $W_i^V \\in \\mathbb{R}^{d_{\\text{model}} \\times d_v}$.'
        }
      ],
      intuition: 'A single attention head averages all relationships together (e.g., syntax, tense, entity resolution). Multi-head attention gives the model $h$ different "eyes": head 1 tracks subject-verb agreement, head 2 tracks co-reference, head 3 tracks temporal context.',
      keyNuances: [
        'Total computational cost is similar to single-head attention with full $d_{\\text{model}}$ because $d_k = d_v = d_{\\text{model}} / h$. For base Transformer: $h=8, d_{\\text{model}}=512 \\implies d_k=64$.',
        'Different heads specialize automatically during training without explicit constraint loss functions.'
      ],
      ancestors: [
        {
          paperTitle: 'Attention Is All You Need',
          authorsYear: 'Vaswani et al., 2017',
          concept: 'Single-Head Scaled Dot-Product',
          relationship: 'Multi-head projects representations into $h$ distinct subspaces prior to dot product.'
        }
      ],
      descendants: [
        {
          concept: 'Grouped-Query Attention (GQA)',
          paperOrField: 'Ainslie et al., 2023',
          impact: 'Shares Key and Value heads across Query groups to reduce memory memory bandwidth during LLM generation.'
        }
      ],
      citations: ['Vaswani et al., 2017, Section 3.2.2']
    },

    {
      id: 'concept-positional-encoding',
      title: 'Sinusoidal Positional Encodings',
      category: 'mathematical_formulation',
      definition: 'Fixed sinusoidal wave functions added directly to input embeddings to inject sequence order information into the permutation-invariant self-attention layers.',
      mathFormulas: [
        {
          label: 'Sinusoidal Position Equations',
          latex: 'PE_{(pos, 2i)} = \\sin\\left(\\frac{pos}{10000^{2i / d_{\\text{model}}}}\\right), \\quad PE_{(pos, 2i+1)} = \\cos\\left(\\frac{pos}{10000^{2i / d_{\\text{model}}}}\\right)',
          explanation: 'Where $pos$ is the token position in the sequence, and $i$ is the feature dimension index ($0 \\le i < d_{\\text{model}}/2$).'
        },
        {
          label: 'Linear Transformation Property',
          latex: 'PE_{pos + k} = M_k \\cdot PE_{pos}',
          explanation: 'For any fixed offset $k$, $PE_{pos+k}$ can be represented as a linear function of $PE_{pos}$, allowing attention to easily learn relative position relationships.'
        }
      ],
      intuition: 'Self-attention calculates token pairs as a set without knowing token order ("Dog bites man" vs "Man bites dog"). Sinusoidal encodings function like a high-resolution binary clock across vector dimensions, assigning a unique geometric signature to every position.',
      keyNuances: [
        'Wavelengths form a geometric progression from $2\\pi$ to $10000 \\cdot 2\\pi$.',
        'Vaswani et al. tested both learned positional embeddings and fixed sinusoidal encodings and found virtually identical performance, but chose sinusoidal because it allows the model to extrapolate to sequence lengths longer than those seen during training.'
      ],
      ancestors: [
        {
          paperTitle: 'Convolutional Sequence to Sequence Learning',
          authorsYear: 'Gehring et al., 2017',
          concept: 'Learned Positional Embeddings',
          relationship: 'Replaced learned position lookup tables with deterministic multi-frequency trigonometric functions.'
        }
      ],
      descendants: [
        {
          concept: 'Rotary Position Embedding (RoPE)',
          paperOrField: 'Su et al., 2021',
          impact: 'Applies rotation matrices directly to Q and K vectors instead of adding encodings to input embeddings.'
        }
      ],
      citations: ['Vaswani et al., 2017, Section 3.5']
    }
  ],

  flashcards: [
    {
      id: 'fc-1',
      conceptId: 'concept-scaled-dot-product',
      question: 'Why do we divide the dot product $Q K^T$ by $\\sqrt{d_k}$ in Scaled Dot-Product Attention?',
      answer: 'For large values of $d_k$, dot products grow large in magnitude, pushing the softmax function into regions with extremely small gradients (vanishing gradients). Assuming $q$ and $k$ are independent random variables with mean 0 and variance 1, their dot product $q \\cdot k = \\sum_{i=1}^{d_k} q_i k_i$ has mean 0 and variance $d_k$. Dividing by $\\sqrt{d_k}$ normalizes the variance to 1, maintaining stable gradients during backpropagation.',
      formula: '\\text{Var}(q \\cdot k) = d_k \\quad \\implies \\quad \\text{Var}\\left(\\frac{q \\cdot k}{\\sqrt{d_k}}\\right) = 1',
      difficulty: 'hard',
      tags: ['Attention', 'Math Derivation', 'Softmax', 'Gradients'],
      srsStage: 0,
      figureUrl: 'https://arxiv.org/html/1706.03762/x1.png',
      figureCaption: 'Figure 2 (Left): Scaled Dot-Product Attention architecture with optional Masking.'
    },
    {
      id: 'fc-2',
      conceptId: 'concept-scaled-dot-product',
      question: 'What are the time and memory computational complexities of Self-Attention with respect to sequence length $n$ and representation dimension $d$?',
      answer: '• **Time Complexity**: $O(n^2 \\cdot d)$ due to multiplying Query matrix $(n \\times d)$ with Key matrix $(d \\times n)$ to produce the $(n \\times n)$ score matrix.\n• **Memory Overhead**: $O(n^2)$ to store the $(n \\times n)$ attention weight matrix for backpropagation.\n• **Max Sequential Operations**: $O(1)$ because all pairwise scores are computed simultaneously via matrix multiplication.',
      formula: '\\text{Time: } O(n^2 \\cdot d), \\quad \\text{Space: } O(n^2), \\quad \\text{Sequential Path: } O(1)',
      difficulty: 'hard',
      tags: ['Complexity', 'Big-O', 'Self-Attention', 'Performance'],
      srsStage: 0
    },
    {
      id: 'fc-3',
      conceptId: 'concept-multi-head-attention',
      question: 'Why does Multi-Head Attention not increase total computational cost compared to single-head attention with full $d_{\\text{model}}$?',
      answer: 'Because the projection dimensions for each head are reduced proportionally: $d_k = d_v = d_{\\text{model}} / h$. \nFor example, if $d_{\\text{model}} = 512$ and $h = 8$, each head operates on vectors of size $d_k = 64$. Performing $h$ parallel attention operations of dimension $d_k$ costs $h \\times O(n^2 \\cdot (d_{\\text{model}}/h)) = O(n^2 \\cdot d_{\\text{model}})$, identical to single-head attention on $d_{\\text{model}}$.',
      formula: 'd_k = d_v = \\frac{d_{\\text{model}}}{h}',
      difficulty: 'medium',
      tags: ['Multi-Head Attention', 'Architecture', 'Dimensions'],
      srsStage: 0,
      figureUrl: 'https://arxiv.org/html/1706.03762/x2.png',
      figureCaption: 'Figure 2 (Right): Multi-Head Attention with h parallel linear projections.'
    },
    {
      id: 'fc-4',
      conceptId: 'concept-positional-encoding',
      question: 'How do Sinusoidal Positional Encodings allow the model to easily attend to relative positions?',
      answer: 'By the trigonometric angle addition identities, for any fixed offset $k$, $PE_{(pos+k)}$ can be expressed as a linear combination of $PE_{(pos)}$:\n$$\\begin{pmatrix} \\sin(\\omega_i (pos+k)) \\\\ \\cos(\\omega_i (pos+k)) \\end{pmatrix} = \\begin{pmatrix} \\cos(\\omega_i k) & \\sin(\\omega_i k) \\\\ -\\sin(\\omega_i k) & \\cos(\\omega_i k) \\end{pmatrix} \\begin{pmatrix} \\sin(\\omega_i pos) \\\\ \\cos(\\omega_i pos) \\end{pmatrix}$$\nThis means the linear query-key projections in self-attention can learn to attend to relative position offsets $k$ through a simple rotation matrix.',
      formula: 'PE_{pos + k} = M_k \\cdot PE_{pos}',
      difficulty: 'extreme',
      tags: ['Positional Encoding', 'Trigonometry', 'Math Derivation'],
      srsStage: 0
    },
    {
      id: 'fc-5',
      conceptId: 'concept-scaled-dot-product',
      question: 'How is causal masking implemented in the Transformer Decoder self-attention layer, and why is it necessary?',
      answer: 'Causal masking sets all illegal future token positions in the scaled attention score matrix to $-\\infty$ before applying softmax. Since $e^{-\\infty} = 0$, future positions receive zero attention weight. \n\nThis is necessary during autoregressive training to prevent leftward information flow, ensuring that prediction for position $i$ depends strictly on known outputs at positions $< i$.',
      formula: '\\text{MaskedScore}_{i,j} = \\begin{cases} \\frac{Q_i K_j^T}{\\sqrt{d_k}} & \\text{if } j \\le i \\\\ -\\infty & \\text{if } j > i \\end{cases}',
      difficulty: 'medium',
      tags: ['Decoder', 'Masking', 'Autoregressive', 'Implementation'],
      srsStage: 0
    },
    {
      id: 'fc-6',
      conceptId: 'concept-multi-head-attention',
      question: 'What is the full equation and block structure for a standard Encoder Layer in the Transformer?',
      answer: 'Each Encoder layer consists of two sub-layers with residual connections and Layer Normalization:\n1. Multi-Head Self-Attention: $x^{(1)} = \\text{LayerNorm}(x + \\text{MultiHead}(x, x, x))$\n2. Position-wise Feed-Forward Network: $x^{(2)} = \\text{LayerNorm}(x^{(1)} + \\text{FFN}(x^{(1)}))$\n\nWhere $\\text{FFN}(z) = \\max(0, z W_1 + b_1) W_2 + b_2$ with inner dimension $d_{ff} = 4 d_{\\text{model}}$.',
      formula: 'SubLayerOutput = \\text{LayerNorm}(x + \\text{SubLayer}(x))',
      difficulty: 'medium',
      tags: ['Encoder', 'Residual', 'LayerNorm', 'FFN'],
      srsStage: 0,
      figureUrl: 'https://arxiv.org/html/1706.03762/x3.png',
      figureCaption: 'Figure 1: Complete Encoder-Decoder Transformer Model Architecture.'
    }
  ]
};
